import { StoreEnvironment, StoreI18n, StoreSettings } from '../../../../Types/StoreTypes';
import ImageUpload from '../../../Utility/ImageUpload';
import { Button } from '../../../Utility/Button';
import { useThemeSettings } from '../../../../Hooks/useThemeSettings';
import Input from '../../../Utility/FormElements/Input';
import { useMemo, useRef, useState, type ChangeEvent } from 'react';
import toast from 'react-hot-toast';

interface GeneralSettingsProps {
    SettingsActions: any;
    i18n: StoreI18n['i18n'];
    windowWidth: StoreEnvironment['windowWidth'];
    getBreakpoint: (breakpoint: string) => number;
    Currentsettings: StoreSettings;
}

function ThemeSettings({ SettingsActions, i18n, Currentsettings }: GeneralSettingsProps) {
    const { imagePreviewDark, imagePreviewLight, handleImageChange, handleRemoveImage } = useThemeSettings(
        Currentsettings,
        SettingsActions,
    );

    const imageUploadWidth = 'w-[300px]';
    const imageUploadHeight = 'h-[100px]';

    const favicon = Currentsettings?.favicon;
    const faviconInputRef = useRef<HTMLInputElement | null>(null);
    const [faviconInputKey, setFaviconInputKey] = useState(0);

    const handleFaviconChange = (e: ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files[0]) {
            const file = e.target.files[0];
            const fileName = (file.name ?? '').toLowerCase();
            const extensionOk = fileName.endsWith('.png') || fileName.endsWith('.ico');
            const mimeOk =
                file.type === 'image/png' ||
                file.type === 'image/x-icon' ||
                file.type === 'image/vnd.microsoft.icon' ||
                file.type === '';

            const maxBytes = 1024 * 1024; // 1 MiB
            if (!(extensionOk && mimeOk)) {
                toast.error(i18n.translate({ id: 'themeSettings.favicon.invalidType', sourceName: 'Settings' }));
                e.target.value = '';
                return;
            }
            if (file.size > maxBytes) {
                toast.error(i18n.translate({ id: 'themeSettings.favicon.tooLarge', sourceName: 'Settings' }));
                e.target.value = '';
                return;
            }
            SettingsActions.setCurrentSettings((prev: any) => ({ ...prev, favicon: file }));
        }
    };

    const handleRemoveFavicon = () => {
        SettingsActions.setCurrentSettings((prev: any) => ({ ...prev, favicon: null }));
        if (faviconInputRef.current) {
            faviconInputRef.current.value = '';
        }
        setFaviconInputKey((k) => k + 1);
    };

    const faviconFilename = useMemo(() => {
        if (!favicon) return '';
        if (typeof favicon === 'string') {
            try {
                const url = new URL(favicon, window.location.origin);
                return url.pathname.split('/').pop() ?? '';
            } catch {
                return favicon.split('/').pop() ?? '';
            }
        }
        return favicon.name ?? '';
    }, [favicon]);

    const brandingTitleValue = useMemo(
        () => (Currentsettings.brandingTitle ?? '').toString(),
        [Currentsettings.brandingTitle],
    );

    return (
        <div className="w-full flex gap-8 flex-col">
            <h4 className="block">{i18n.translate({ id: 'themeSettings', sourceName: 'Settings' })}</h4>

            <div className="flex flex-col w-full gap-5">
                <div className="w-full">
                    <label htmlFor="settings_brandingTitle" className="flex flex-col gap-2">
                        {i18n.translate({ id: 'themeSettings.brandingTitle', sourceName: 'Settings' })}
                        <Input
                            inputType="string"
                            id="settings_brandingTitle"
                            value={brandingTitleValue}
                            onChange={(value) =>
                                SettingsActions.updateCurrentSettings({
                                    brandingTitle: value.trim() === '' ? null : value,
                                })
                            }
                            placeholder={'VARIOS AI'}
                        />
                    </label>
                </div>

                <div className="w-full">
                    <label className="block mb-2">
                        {i18n.translate({ id: 'themeSettings.favicon', sourceName: 'Settings' })}
                    </label>
                    <div className="flex flex-col gap-3">
                        <input
                            key={faviconInputKey}
                            ref={faviconInputRef}
                            type="file"
                            accept="image/png,image/x-icon,image/vnd.microsoft.icon,.png,.ico"
                            onChange={handleFaviconChange}
                            aria-label={i18n.translate({ id: 'themeSettings.favicon', sourceName: 'Settings' })}
                            className="bg-input-backgroundColor w-full p-3 border border-2 border-input-borderColor rounded-lg outline-none focus:border-primary transition-all duration-100"
                        />
                        {favicon ? (
                            <div className="flex items-center justify-between gap-3">
                                {typeof favicon === 'string' ? (
                                    <div className="text-sm text-layout-textcolor break-all">{faviconFilename}</div>
                                ) : (
                                    <div />
                                )}
                                <Button variant="secondaryOutlined" onClick={handleRemoveFavicon}>
                                    {i18n.translate({ id: 'themeSettings.favicon.remove', sourceName: 'Settings' })}
                                </Button>
                            </div>
                        ) : null}
                    </div>
                </div>

                <div className="w-full">
                    <label className="block mb-2">
                        {i18n.translate({ id: 'themeSettings.logoDarkBackground', sourceName: 'Settings' })}
                    </label>

                    <div className={`border p-0 ${imageUploadWidth} ${imageUploadHeight} border-layout-borderColor`}>
                        <ImageUpload
                            variant="box"
                            handleImageChange={(e) => {
                                handleImageChange(e, 'dark');
                            }}
                            handleRemoveImage={() => handleRemoveImage('dark')}
                            camRemoveImage={true}
                            imagePreview={imagePreviewDark}
                            customSettings={{
                                customBackgroundColor: 'bg-gray-900',
                                customForegroundColor: 'text-gray-100',
                                customHeight: imageUploadHeight,
                                customWidth: imageUploadWidth,
                            }}
                        ></ImageUpload>
                    </div>
                </div>

                <div className="w-full">
                    <label className="block mb-2">
                        {i18n.translate({ id: 'themeSettings.logoLightBackground', sourceName: 'Settings' })}
                    </label>

                    <div className={`border p-0 ${imageUploadWidth} ${imageUploadHeight} border-layout-borderColor`}>
                        <ImageUpload
                            variant="box"
                            handleImageChange={(e) => {
                                handleImageChange(e, 'light');
                            }}
                            handleRemoveImage={() => handleRemoveImage('light')}
                            camRemoveImage={true}
                            imagePreview={imagePreviewLight}
                            customSettings={{
                                customBackgroundColor: 'bg-gray-100',
                                customForegroundColor: 'text-gray-900',
                                customHeight: imageUploadHeight,
                                customWidth: imageUploadWidth,
                            }}
                        ></ImageUpload>
                    </div>
                </div>
            </div>

            <div className="mt-5 text-right">
                <Button onClick={SettingsActions.saveSettings}>
                    {i18n.translate({ id: 'save', sourceName: 'Main' })}
                </Button>
            </div>
        </div>
    );
}

export default ThemeSettings;
